/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useCallback, useEffect, useRef } from 'react'
import {
  and,
  ArrayLayoutProps,
  composePaths,
  createDefaultValue,
  isObjectArrayWithNesting,
  JsonSchema4,
  JsonSchema7,
  RankedTester,
  rankWith,
  schemaMatches
} from '@jsonforms/core'

import {
  ResolvedJsonFormsDispatch,
  withJsonFormsArrayLayoutProps
} from '@jsonforms/react'
import { useAppSelector } from 'hooks/store'
import { MatterClientPortalQuestionnaireSectionStatus } from 'share/domain/case'

type SchemaWithCustomArrayField = (JsonSchema4 | JsonSchema7) & {
  customArray?: boolean
}

export const ArrayField: React.FC<ArrayLayoutProps> = ({
  visible,
  uischema,
  schema,
  renderers,
  cells,
  data,
  path,
  addItem,
  removeItems
}: ArrayLayoutProps) => {
  const addButtonRef = useRef(null)
  const activeSection = useAppSelector((state) => state.activeSection)
  const activeSectionStatus = activeSection.section?.sectionStatus

  const elements = uischema.options?.['detail']['elements']

  const customArraySchema = schema as SchemaWithCustomArrayField

  const removeItemsCb = useCallback(
    (path: string, toDelete: number[]) => {
      if (removeItems) {
        return removeItems(path, toDelete)
      }
    },
    [removeItems]
  )

  const mouseClickEvents = ['mousedown', 'click', 'mouseup']
  const simulateMouseClick = (element: Element | null) => {
    mouseClickEvents.forEach((mouseEventType) => {
      if (element) {
        element.dispatchEvent(
          new MouseEvent(mouseEventType, {
            view: window,
            bubbles: true,
            cancelable: true,
            buttons: 1
          })
        )
      }
    })
  }

  useEffect(() => {
    let timeOut: any
    if (visible) {
      if (addButtonRef && Array(data).length === 0) {
        timeOut = setTimeout(() => {
          simulateMouseClick(addButtonRef.current)
        }, 500)
      }
    }

    return () => {
      if (timeOut) {
        clearTimeout(timeOut)
      }
    }
  }, [visible])

  const itemsToRender = Array(data)
    .fill(0)
    .map((_, i) => {
      return (
        <div key={i}>
          {elements.map((element: any) => {
            element = { ...element, label: element.label + ' ' + (i + 1) }
            return (
              <React.Fragment key={element?.scope}>
                <div style={{ position: 'relative' }}>
                  {activeSectionStatus !==
                    MatterClientPortalQuestionnaireSectionStatus.Submitted &&
                  activeSectionStatus !==
                    MatterClientPortalQuestionnaireSectionStatus.Imported ? (
                    <div
                      style={{
                        position: 'absolute',
                        top: '0px',
                        right: '0px',
                        fontWeight: 500,
                        fontSize: '14px',
                        color: '#2A7AB7',
                        cursor: 'pointer'
                      }}
                      onClick={removeItemsCb(path, [i])}
                      data-testid='arrayfield-delete'
                    >
                      Delete
                    </div>
                  ) : (
                    <></>
                  )}
                  <ResolvedJsonFormsDispatch
                    schema={customArraySchema}
                    uischema={element}
                    path={composePaths(path, `${i}`)}
                    enabled={true}
                    renderers={renderers}
                    cells={cells}
                    key={element?.scope}
                  />
                </div>
              </React.Fragment>
            )
          })}
        </div>
      )
    })

  const addItemCb = useCallback(
    (p: string, value: any) => {
      value = {
        ...value
      }

      return addItem(p, value)
    },
    [addItem]
  )

  return (
    <div hidden={!visible}>
      <div
        className='array-label'
        style={{
          fontWeight: 500,
          fontSize: '14px',
          color: '#173237',
          cursor: 'pointer'
        }}
      >
        {(uischema as any).label ? (
          <section
            style={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#173237',
              marginBottom: '1rem',
              width: '90%'
            }}
          >
            {(uischema as any).label}
          </section>
        ) : (
          <></>
        )}
      </div>
      {itemsToRender}
      {activeSectionStatus !==
        MatterClientPortalQuestionnaireSectionStatus.Submitted &&
      activeSectionStatus !==
        MatterClientPortalQuestionnaireSectionStatus.Imported ? (
        <div>
          <div
            id='add-btn'
            ref={addButtonRef}
            onClick={addItemCb(path, createDefaultValue(schema))}
            style={{
              fontWeight: 500,
              fontSize: '14px',
              color: '#2A7AB7',
              cursor: 'pointer'
            }}
          >
            {(uischema as any).addLabel ? (
              <section
                style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#2A7AB7',
                  marginBottom: '1rem'
                }}
                data-testid='arrayfield-add-label'
              >
                {(uischema as any).addLabel}
              </section>
            ) : (
              <></>
            )}
          </div>
        </div>
      ) : (
        <></>
      )}
    </div>
  )
}

export const arrayFieldTester: RankedTester = rankWith(
  6,
  and(
    isObjectArrayWithNesting,
    schemaMatches((schema: SchemaWithCustomArrayField) => {
      if (Object.prototype.hasOwnProperty.call(schema, 'customArray')) {
        return true
      }
      return false
    })
  )
)

export default withJsonFormsArrayLayoutProps(ArrayField)
